import { useState } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import styles from "./pat-dialog.module.css";
import { formatDate } from "@/lib/format";

const DURATIONS = [
  { label: "1 day",  days: 1 },
  { label: "7 days", days: 7 },
  { label: "30 days", days: 30 },
];

export default function PatDialog({ onClose }: { onClose: () => void }) {
  const [selected, setSelected] = useState<number>(30);
  const [custom, setCustom] = useState("");
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [expires, setExpires] = useState("");
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");

  const effectiveDays = selected === -1 ? parseInt(custom, 10) : selected;

  async function generate() {
    if (!effectiveDays || effectiveDays < 1 || effectiveDays > 365) {
      setError("Duration must be between 1 and 365 days");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const res = await api.generatePat(effectiveDays);
      setToken(res.token);
      setExpires(formatDate(res.expires_at));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Error generating token");
    } finally {
      setLoading(false);
    }
  }

  function copyToken() {
    if (!token) return;
    navigator.clipboard.writeText(token);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return createPortal(
    <div
      className={styles.overlay}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className={styles.dialog}>
        <div className={styles.header}>
          <span className={styles.title}>Generate Personal Access Token</span>
          <button className={styles.closeBtn} onClick={onClose}>×</button>
        </div>

        {!token ? (
          <>
            <div className={styles.durationBlock}>
              <label className={styles.durationLabel}>Duration</label>
              <div className={styles.durationBtns}>
                {DURATIONS.map((d) => {
                  const active = selected === d.days;
                  return (
                    <button
                      key={d.days}
                      className={`${styles.durationBtn} ${active ? styles.durationBtnActive : ""}`}
                      onClick={() => { setSelected(d.days); setCustom(""); }}
                    >
                      {d.label}
                    </button>
                  );
                })}
                <button
                  className={`${styles.durationBtn} ${selected === -1 ? styles.durationBtnActive : ""}`}
                  onClick={() => setSelected(-1)}
                >
                  Custom
                </button>
              </div>
              {selected === -1 && (
                <div className={styles.customRow}>
                  <input
                    type="number"
                    min={1}
                    max={365}
                    value={custom}
                    onChange={(e) => setCustom(e.target.value)}
                    placeholder="days"
                    className={styles.customInput}
                  />
                  <span className={styles.customHint}>days (max. 365)</span>
                </div>
              )}
            </div>

            {error && <div className={styles.errorMsg}>{error}</div>}

            <button
              className={styles.generateBtn}
              onClick={generate}
              disabled={loading}
            >
              {loading ? "Generating…" : "Generate token"}
            </button>
          </>
        ) : (
          <>
            <div className={styles.tokenBox}>{token}</div>

            <div className={styles.actionRow}>
              <button
                className={`${styles.copyBtn} ${copied ? styles.copyBtnCopied : ""}`}
                onClick={copyToken}
              >
                {copied ? "Copied!" : "Copy"}
              </button>
              <button className={styles.closeActionBtn} onClick={onClose}>
                Close
              </button>
            </div>

            <div className={styles.infoBox}>
              <strong className={styles.infoBoxStrong}>Valid until:</strong> {expires}<br />
              Use it in the header <code className={styles.infoCode}>Authorization: Bearer &lt;token&gt;</code><br />
              If you lose it, generate a new one from here.
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
